import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, Card, CardBody, CardHeader, Flex, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import moment from "moment";

import { buildOwnerPaymentsView } from "../../../utils/ownerPayments";
import SearchBox from "../../Properties/SearchBox";

import PropertyPaymentsCard from "./PropertiesPaymentCard";

export default function Payments() {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [year, setYear] = useState(moment().year());
	// Properties with unpaid payments start expanded. `opened` and `closed` only
	// hold the cards the owner clicked, so that default keeps working for the rest.
	const [opened, setOpened] = useState(() => new Set());
	const [closed, setClosed] = useState(() => new Set());

	const storeProperties = useSelector((state) => state.user?.user?.properties);
	const properties = useMemo(() => (storeProperties || []).filter((property) => !property.archived), [storeProperties]);
	const view = useMemo(() => buildOwnerPaymentsView({ properties, year, query }), [properties, year, query]);

	const muted = useColorModeValue("gray.600", "gray.300");

	const isExpanded = useCallback(
		(entry) => opened.has(entry.propertyId) || (entry.unpaidCount > 0 && !closed.has(entry.propertyId)),
		[opened, closed],
	);

	const toggleProperty = (entry) => {
		const expanded = isExpanded(entry);
		setOpened((previous) => {
			const next = new Set(previous);
			if (expanded) next.delete(entry.propertyId);
			else next.add(entry.propertyId);
			return next;
		});
		setClosed((previous) => {
			const next = new Set(previous);
			if (expanded) next.add(entry.propertyId);
			else next.delete(entry.propertyId);
			return next;
		});
	};

	const allExpanded = view.properties.length > 0 && view.properties.every(isExpanded);

	const toggleAll = () => {
		if (allExpanded) {
			setOpened(new Set());
			setClosed(new Set(view.properties.map((entry) => entry.propertyId)));
		} else {
			setOpened(new Set(view.properties.map((entry) => entry.propertyId)));
			setClosed(new Set());
		}
	};

	return (
		<Stack spacing={3}>
			<Card>
				<CardHeader>
					<Flex alignItems="center" justifyContent="space-between" gap={4} flexWrap="wrap">
						<Stack spacing={0}>
							<Text fontSize="xl" fontWeight="bold">
								{t("navbarMenu.payments")}
							</Text>
							<Text fontSize="sm" color={muted}>
								{t("payments.summary", { properties: view.propertyCount, unpaid: view.unpaidCount })}
							</Text>
						</Stack>
						<SearchBox
							handleSearch={setQuery}
							searchOptions={[{ label: t("table.address"), description: t("payments.searchByAddress") }]}
						/>
					</Flex>
				</CardHeader>
				<CardBody pt={0}>
					<Flex alignItems="center" gap={4} flexWrap="wrap">
						<Button size="sm" onClick={() => setYear(year - 1)}>
							{t("table.previousYear")}
						</Button>
						<Text fontWeight="bold" minW="4rem" textAlign="center">
							{year}
						</Text>
						<Button size="sm" isDisabled={year >= moment().year()} onClick={() => setYear(year + 1)}>
							{t("table.nextYear")}
						</Button>
						<Button size="xs" onClick={toggleAll} isDisabled={view.properties.length === 0}>
							{allExpanded ? t("payments.collapseAll") : t("payments.expandAll")}
						</Button>
						{view.hiddenCount > 0 && (
							<Text fontSize="xs" color={muted} ml="auto">
								{t("payments.hiddenBySearch", { count: view.hiddenCount })}
							</Text>
						)}
					</Flex>
				</CardBody>
			</Card>

			{view.properties.map((entry) => (
				<PropertyPaymentsCard
					key={entry.propertyId}
					entry={entry}
					year={view.year}
					isExpanded={isExpanded(entry)}
					onToggle={() => toggleProperty(entry)}
				/>
			))}

			{view.properties.length === 0 && (
				<Card>
					<CardBody>
						<Text fontSize="sm" color={muted}>
							{view.propertyCount === 0 ? t("payments.noProperties") : t("payments.noMatches")}
						</Text>
					</CardBody>
				</Card>
			)}
		</Stack>
	);
}
